'use client';

import { useEffect, useRef, useState } from 'react';

import LoginModal from '@/widgets/LoginModal';
import { ProductDetailsTabValue } from '@/widgets/ProductDetail/_components/ProductDetailsTabs/models';
import ReviewModal from '@/widgets/ReviewModal';

import { isUserLogin } from '@/utilities/CheckUserIsLogin';
import { getDefaultFulfillmentProfile } from '@/utilities/fulfillment';
import { replaceStoreQuery } from '@/utilities/replaceStoreQuery';
import { resolveSelectedSeller } from '@/utilities/resolveSelectedSeller';

import { CAR_BATTERY_VEHICLE_TYPE_ID, MOTOR_BATTERY_VEHICLE_TYPE_ID } from '@/constants/batteryProduct';
import { productRequestRouteByVehicleType } from '@/constants/product';

import { BuyBox } from './_components/BuyBox';
import { DeliveryMethods } from './_components/DeliveryMethods';
import { FaqSection } from './_components/FaqSection';
import { Gallery } from './_components/Gallery';
import { MobileBuyBar } from './_components/MobileBuyBar';
import { MobileDeliverySheet } from './_components/MobileDeliverySheet';
import { OtherSellers } from './_components/OtherSellers';
import { ProductDetailsTabs } from './_components/ProductDetailsTabs';
import { ProductMeta } from './_components/ProductMeta';
import { TitleBlock } from './_components/TitleBlock';
import { RatingSummaryValue } from './_components/TitleBlock/models';
import { ProductDetailProps } from './models';

import { getReviewsByreferenceTypeService } from '@/client/services/review/methods';
import { ActiveOfferContent, FulfillmentProfile } from '@/server/services/vendor/models';

export const ProductDetail = ({
    title,
    subtitle,
    description,
    attributes,
    seoDescription,
    badges,
    vehicleType,
    uuid,
    category,
    brand,
    guarantee,
    ampere,
    pole,
    height,
    children,
    isPriceLoading,
    allStores,
    winner,
    faqItems = [],
    isAttributesLoading,
    preferredStoreUuid
}: ProductDetailProps) => {
    const [ratingSummary, setRatingSummary] = useState<RatingSummaryValue | null>(null);
    const [isRatingLoading, setIsRatingLoading] = useState(true);
    const [isDeliverySheetOpen, setIsDeliverySheetOpen] = useState(false);
    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [selectedSeller, setSelectedSeller] = useState<ActiveOfferContent>(() => resolveSelectedSeller(winner, allStores, preferredStoreUuid));
    const [activeTab, setActiveTab] = useState<ProductDetailsTabValue | null>(null);
    const [selectedFulfillmentProfile, setSelectedFulfillmentProfile] = useState<FulfillmentProfile | null>(() =>
        getDefaultFulfillmentProfile(resolveSelectedSeller(winner, allStores, preferredStoreUuid))
    );
    const [syncedWinner, setSyncedWinner] = useState(winner);
    const [syncedAllStores, setSyncedAllStores] = useState(allStores);
    const [syncedPreferredStoreUuid, setSyncedPreferredStoreUuid] = useState(preferredStoreUuid);
    const [storeUuid, setStoreUuid] = useState(preferredStoreUuid);
    const mobileBuyBoxRef = useRef<HTMLDivElement | null>(null);
    const sellersRef = useRef<HTMLDivElement | null>(null);

    if (winner !== syncedWinner || allStores !== syncedAllStores || preferredStoreUuid !== syncedPreferredStoreUuid) {
        const nextStoreUuid = preferredStoreUuid !== syncedPreferredStoreUuid ? preferredStoreUuid : storeUuid;
        const nextSeller = resolveSelectedSeller(winner, allStores, nextStoreUuid);
        setSyncedWinner(winner);
        setSyncedAllStores(allStores);
        setSyncedPreferredStoreUuid(preferredStoreUuid);
        if (preferredStoreUuid !== syncedPreferredStoreUuid) {
            setStoreUuid(preferredStoreUuid);
        }
        setSelectedSeller(nextSeller);
        setSelectedFulfillmentProfile(getDefaultFulfillmentProfile(nextSeller));
    }

    useEffect(() => {
        let cancelled = false;
        setIsRatingLoading(true);

        getReviewsByreferenceTypeService({
            referenceType: 'PRODUCT_UUID',
            referenceId: uuid,
            page: 0,
            size: 20
        })
            .then((response) => {
                if (cancelled) return;

                const reviews = response.data.content || [];
                if (!reviews.length) {
                    setRatingSummary(null);
                    return;
                }

                const sum = reviews.reduce((acc, review) => acc + review.rate, 0);
                setRatingSummary({
                    average: sum / reviews.length,
                    count: response.data.totalElements ?? reviews.length
                });
            })
            .catch(() => {
                if (!cancelled) setRatingSummary(null);
            })
            .finally(() => {
                if (!cancelled) setIsRatingLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [uuid]);
    useEffect(() => {
        if (isAttributesLoading) return;
        setActiveTab(description ? 'intro' : attributes.length > 0 ? 'specs' : 'reviews');
    }, [isAttributesLoading]);

    const priceLoading = Boolean(isPriceLoading);
    const selectedStore = allStores.find((store) => store.offerId === selectedSeller?.offerId) ?? selectedSeller;
    const selectedSellerAvailable =
        Boolean(selectedSeller?.offerId) && selectedStore?.availableQuantity > 0 && Boolean(getDefaultFulfillmentProfile(selectedStore));
    const otherVendorsCount = Math.max(allStores.length - 1, 0);
    const isLogin = isUserLogin();
    const isMainVendor = category.slug === CAR_BATTERY_VEHICLE_TYPE_ID || category.slug === MOTOR_BATTERY_VEHICLE_TYPE_ID;
    const canAddReview = !isMainVendor;

    const scrollAfterSellerSelection = () => {
        if (window.matchMedia('(max-width: 1023px)').matches) {
            mobileBuyBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
        }

        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSelectSeller = (seller: ActiveOfferContent) => {
        if (seller.availableQuantity <= 0 || !getDefaultFulfillmentProfile(seller)) return;

        if (seller.offerId === selectedSeller.offerId) {
            scrollAfterSellerSelection();
            return;
        }

        setStoreUuid(seller.storeUuid);
        setSelectedSeller(seller);
        setSelectedFulfillmentProfile(getDefaultFulfillmentProfile(seller));
        replaceStoreQuery(seller.storeUuid);
        scrollAfterSellerSelection();
    };

    const scrollToSellers = () => {
        sellersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handleAddReview = () => {
        if (!canAddReview) return;

        if (isLogin) {
            setIsReviewModalOpen(true);
        } else {
            setShowLoginModal(true);
        }
    };

    const handleUserLogin = () => {
        setShowLoginModal(false);
        setIsReviewModalOpen(true);
    };

    const addToCartProps = {
        href: `/${vehicleType}${productRequestRouteByVehicleType[vehicleType]}?productId=${uuid}`,
        isMainVendor,
        isPriceLoading: priceLoading
    };

    return (
        <div className='pb-32 lg:pb-0'>
            <article className='flex flex-col gap-6'>
                <div className='grid grid-cols-1 items-start gap-6 md:grid-cols-[5fr_7fr] lg:grid-cols-[5fr_13fr]'>
                    <div className='min-w-0 md:sticky md:top-20 md:self-start'>
                        <Gallery
                            uuid={uuid}
                            title={title}
                            seoDescription={seoDescription}
                            available={selectedSellerAvailable}
                            isPriceLoading={priceLoading}
                            discountPercentage={selectedSeller.discountPercentage}
                        />
                    </div>

                    <div className='flex min-w-0 flex-col'>
                        <TitleBlock title={title} brand={brand} category={category} />

                        <div className='flex flex-col-reverse gap-4'>
                            <div className='block lg:hidden'>
                                <ProductMeta
                                    badges={badges}
                                    subtitle={subtitle}
                                    ratingSummary={ratingSummary}
                                    available={selectedSellerAvailable}
                                    availableQuantity={selectedSellerAvailable ? selectedSeller.availableQuantity : 0}
                                    isPriceLoading={priceLoading}
                                    isRatingLoading={isRatingLoading}
                                    onAddReview={handleAddReview}
                                    canAddReview={canAddReview}
                                />
                                <hr className='my-3 opacity-10' />
                            </div>
                        </div>
                        <div className='grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start'>
                            <div className='min-w-0'>
                                <div ref={mobileBuyBoxRef} className='block scroll-mt-20 lg:hidden'>
                                    <BuyBox
                                        winner={selectedSeller}
                                        allStores={allStores}
                                        available={selectedSellerAvailable}
                                        isPriceLoading={priceLoading}
                                        category={category}
                                        guarantee={guarantee}
                                        vehicleType={vehicleType}
                                        uuid={uuid}
                                        onViewAllVendors={scrollToSellers}
                                        selectedFulfillmentProfile={selectedFulfillmentProfile}
                                    />
                                </div>
                                <div className='hidden lg:block'>
                                    <ProductMeta
                                        badges={badges}
                                        subtitle={subtitle}
                                        ratingSummary={ratingSummary}
                                        available={selectedSellerAvailable}
                                        availableQuantity={selectedSellerAvailable ? selectedSeller.availableQuantity : 0}
                                        isPriceLoading={priceLoading}
                                        isRatingLoading={isRatingLoading}
                                        onAddReview={handleAddReview}
                                        canAddReview={canAddReview}
                                        onReviewsClick={() => setActiveTab('reviews')}
                                    />
                                </div>
                                <DeliveryMethods
                                    seller={selectedStore}
                                    value={selectedFulfillmentProfile}
                                    onChange={setSelectedFulfillmentProfile}
                                    disabled={!selectedSellerAvailable || priceLoading}
                                    unavailable={!selectedSellerAvailable && !priceLoading}
                                    isLoading={priceLoading}
                                />
                            </div>
                            <div className='hidden lg:block'>
                                <BuyBox
                                    winner={selectedSeller}
                                    allStores={allStores}
                                    available={selectedSellerAvailable}
                                    isPriceLoading={priceLoading}
                                    category={category}
                                    guarantee={guarantee}
                                    vehicleType={vehicleType}
                                    uuid={uuid}
                                    onViewAllVendors={scrollToSellers}
                                    selectedFulfillmentProfile={selectedFulfillmentProfile}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {!!children && children}
            </article>
            <hr className='my-3 opacity-10' />

            <div ref={sellersRef} className='mt-6 scroll-mt-20'>
                <OtherSellers
                    allStores={allStores}
                    winner={winner}
                    selectedSeller={selectedSeller}
                    onSelectSeller={handleSelectSeller}
                    addToCartProps={addToCartProps}
                />
            </div>

            <div className='mt-6 flex flex-col gap-6'>
                <ProductDetailsTabs
                    description={description}
                    attributes={attributes}
                    reviewsUuid={uuid}
                    onAddReview={handleAddReview}
                    canAddReview={canAddReview}
                    onSummaryChange={setRatingSummary}
                    ratingSummary={ratingSummary}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                />
                <FaqSection items={faqItems} />
            </div>

            <MobileBuyBar
                winner={selectedSeller}
                allStores={allStores}
                available={selectedSellerAvailable}
                isPriceLoading={priceLoading}
                category={category}
                vehicleType={vehicleType}
                uuid={uuid}
                selectedFulfillmentProfile={selectedFulfillmentProfile}
                onOpenDeliverySheet={() => setIsDeliverySheetOpen(true)}
            />

            <MobileDeliverySheet
                open={isDeliverySheetOpen}
                onClose={() => setIsDeliverySheetOpen(false)}
                seller={selectedStore}
                value={selectedFulfillmentProfile}
                onChange={setSelectedFulfillmentProfile}
                otherVendorsCount={otherVendorsCount}
                onSwitchSeller={scrollToSellers}
                disabled={!selectedSellerAvailable || priceLoading}
                unavailable={!selectedSellerAvailable && !priceLoading}
            />

            {canAddReview && (
                <ReviewModal
                    orderId={null}
                    subOrderId={null}
                    open={isReviewModalOpen}
                    onClose={() => setIsReviewModalOpen(false)}
                    productUuid={uuid}
                    productTitle={title}
                />
            )}

            {canAddReview && showLoginModal && <LoginModal onUserLogin={handleUserLogin} onClose={() => setShowLoginModal(false)} />}
        </div>
    );
};
